/* Shared storefront policy: external HTTP(S) links open in a new tab.
 * No navigation interception; forms, mailto, tel and same-store links are left alone.
 * Third-party iframe contents and closed shadow roots are controlled by their providers.
 */
(() => {
  'use strict';
  const script = document.querySelector('script[data-buti-external-links]');
  const normalizeHost = (host) => host.toLowerCase().replace(/^www\./, '');
  const storeHosts = new Set(
    [window.location.hostname, ...(script?.dataset.storeHosts || '').split(',')]
      .filter(Boolean)
      .map(normalizeHost)
  );
  const managed = new WeakMap();

  function updateLink(link) {
    let external = false;
    try {
      const url = new URL(link.getAttribute('href'), document.baseURI);
      external = /^https?:$/.test(url.protocol) && !storeHosts.has(normalizeHost(url.hostname));
    } catch (_) {
      // Invalid and incomplete URLs are not navigation targets for this policy.
    }

    if (external) {
      if (!managed.has(link)) {
        managed.set(link, {
          target: link.getAttribute('target'),
          addedNoopener: !link.relList.contains('noopener')
        });
      }
      if (link.target !== '_blank') link.target = '_blank';
      if (!link.relList.contains('noopener')) link.relList.add('noopener');
    } else if (managed.has(link)) {
      const previous = managed.get(link);
      if (link.target === '_blank') {
        if (previous.target === null) link.removeAttribute('target');
        else link.setAttribute('target', previous.target);
      }
      if (previous.addedNoopener) link.relList.remove('noopener');
      managed.delete(link);
    }
  }

  function scan(root) {
    if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_NODE) return;
    if (root.matches?.('a[href]')) updateLink(root);
    root.querySelectorAll('a[href]').forEach(updateLink);
  }

  scan(document);
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes') {
        if (mutation.target.matches('a')) updateLink(mutation.target);
      } else {
        mutation.addedNodes.forEach(scan);
      }
    }
  }).observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['href']
  });

  // Also covers links whose href changes immediately before activation.
  function beforeActivation(event) {
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (link) updateLink(link);
  }
  document.addEventListener('click', beforeActivation, true);
  document.addEventListener('auxclick', beforeActivation, true);
})();
